import React, { memo } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';

import { EditorControlsBanner } from '@confluence/editor-action-bar/entry-points/EditorControlsBanner.ts';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader.ts';
import { useIsControlsBannerDismissed } from '@confluence/editor-action-bar/entry-points/useIsControlsBannerDismissed.ts';
import { useOnboardingBannerLogic } from '@confluence/onboarding-editor';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features/entry-points/TriggerAnalyticsEventType.ts';
import { fg } from '@confluence/feature-gating';
import { ExcludeNewConfluenceActiveUser } from '@confluence/targeting';

export type Props = {
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	viewMode?: boolean;
	spaceKey: string;
	editorFullPagePresetAPI: PublicPluginAPI<[SelectionToolbarPlugin]> | undefined;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	contentId: string;
	version: number;
	isShowingOfflineBanner: boolean;
};

export const FullPageEditorBannerWrapper = memo(
	({
		isLivePage,
		isEmbeddedConfluence,
		viewMode,
		spaceKey,
		triggerAnalyticsEvent,
		contentId,
		version,
		isShowingOfflineBanner,
	}: Props) => {
		const { isControlsBannerDismissed, onDismiss, bannerType } =
			useIsControlsBannerDismissed(isLivePage);

		const { isVisible: isOnboardingEditorDraftBannerVisible } = useOnboardingBannerLogic({
			contentId,
			version,
			isShowingOfflineBanner,
			spaceKey,
			setIsOnboardingBannerDataLoaded: () => {},
		});
		const shouldShowControlsBanner =
			!viewMode &&
			!isControlsBannerDismissed &&
			spaceKey !== 'XFLOW' &&
			!isOnboardingEditorDraftBannerVisible &&
			!fg('platform_editor_control_remove_banner'); // remove change boarding banner for all customers. Remove the EditorControlsBanner completely when cleaning up the flag.

		if (shouldShowControlsBanner) {
			if (fg('cc_activation_hide_changeboarding_for_new_users')) {
				return (
					<ExcludeNewConfluenceActiveUser>
						<EditorControlsBanner
							onDismiss={onDismiss}
							triggerAnalyticsEvent={triggerAnalyticsEvent}
							isLivePage={isLivePage}
							isEmbeddedConfluence={isEmbeddedConfluence}
							bannerType={bannerType}
						/>
					</ExcludeNewConfluenceActiveUser>
				);
			}
			return (
				<EditorControlsBanner
					onDismiss={onDismiss}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					isLivePage={isLivePage}
					isEmbeddedConfluence={isEmbeddedConfluence}
					bannerType={bannerType}
				/>
			);
		}

		if (spaceKey !== 'XFLOW' && !isLivePage && fg('aifc_create_enabled')) {
			return <SmartCreateFeedbackBannerLoader contentType="page" />;
		}
	},
);
